// BandMenu.tsx — ONE control where the fleet has two circles.
//
// 🔴 IDENTITY (2026-08-30): "wordmark-left + two-circular-controls-right" is one of the four
// banned fleet fingerprints. Skein lost its 🧶 + "Skein" wordmark (the cast-on verdict is the
// header now — the answer leads), and the two round menu buttons collapse here into a single
// control drawn in this app's own material — a SKEIN BAND: square, with the dyed paper band
// wrapped round both edges. Deliberately not a third circle, and not any sibling's shape.
//
// The shared BackupMenu / AccountMenu are rendered UNCHANGED inside — the account-deletion flow
// the store gate depends on lives inside AccountMenu and must not be reimplemented here.

import { useState, type ReactNode } from "react";
import { platform } from "./tauri-web";
import { WalletTopUp } from "./iap";

export function BandMenu({ backup, account }: { backup: ReactNode; account: ReactNode }) {
  const [open, setOpen] = useState(false);
  // Apple 2.1(b) (Focus/Gifted rejections, 2026-09-05): the wallet top-up must be visible
  // the moment this drawer opens, not nested one tap deeper inside AccountMenu's own avatar
  // popover. Rendered directly here, gated iOS/MAS same as AccountMenu's own gate.
  const isIos = platform() === "ios";
  const isMasMac = platform() === "macos" && (import.meta as any).env?.VITE_MAS === "1";
  return (
    <div style={{ position: "relative" }}>
      <button type="button" className="sk-bandmenu" aria-label="Settings, backup and account" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="3.1" stroke="currentColor" strokeWidth="1.8" />
          <path d="M12 3.7v2.2M12 18.1v2.2M3.7 12h2.2M18.1 12h2.2M6.2 6.2l1.5 1.5M16.3 16.3l1.5 1.5M17.8 6.2l-1.5 1.5M7.7 16.3l-1.5 1.5"
                stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div className="sk-menu-panel">
            {(isIos || isMasMac) && (
              <>
                <div style={{ padding: "0.5rem 0.5rem 0.15rem" }}><WalletTopUp /></div>
                <div className="sk-menu-rule" />
              </>
            )}
            <div className="sk-menu-row">{backup}<span>Back up &amp; restore</span></div>
            <div className="sk-menu-rule" />
            <div className="sk-menu-row">{account}<span>Account</span></div>
          </div>
        </>
      )}
    </div>
  );
}

// 🔴 THE ASSISTANT ENTRY — a yarn ball on the queue rail, replacing the floating "Chat" pill that
// ~15 siblings shared. onAsk opens the same Chat sheet: moved, never removed.
export function AskBall({ onAsk }: { onAsk: () => void }) {
  return (
    <button type="button" className="sk-ball" onClick={onAsk} aria-label="Open chat assistant">
      <span className="sk-ball-mark" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="17" height="17" fill="none">
          <circle cx="12" cy="12" r="8.4" stroke="currentColor" strokeWidth="1.6" />
          <path d="M5.2 8.2c3.6 1.6 7.4 4.6 9.6 8.6M8.2 4.4c2.4 3 4.6 7.4 5.4 12.2M16.9 5.6c-2.6 2.6-5.4 7-6.6 11.4"
                stroke="currentColor" strokeWidth="1.3" />
        </svg>
      </span>
      <span className="sk-ball-txt">
        <span className="sk-ball-lbl">Ask</span>
        <span className="sk-ball-sub">stash · queue · recap</span>
      </span>
    </button>
  );
}
